import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./ClientsPartners.css";

gsap.registerPlugin(ScrollTrigger);

function ClientsPartners() {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const logosRef = useRef([]);
  const velocitiesRef = useRef([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const logos = [
    { 
      id: 1, 
      name: "Gauteng Provincial Government", 
      logo: "gp.png", 
      type: "client",
      size: 120,
      color: "rgba(52, 152, 219, 0.2)"
    },
    { 
      id: 2, 
      name: "University of Johannesburg", 
      logo: "uj.png", 
      type: "client",
      size: 100,
      color: "rgba(46, 204, 113, 0.2)"
    },
    { 
      id: 3, 
      name: "Phanda Studios", 
      logo: "phanda.png", 
      type: "client",
      size: 90,
      color: "rgba(155, 89, 182, 0.2)"
    },
    { 
      id: 4, 
      name: "Young African Entrepreneurs Institute", 
      logo: "yaei.jpeg", 
      type: "partner",
      size: 110,
      color: "rgba(241, 196, 15, 0.2)"
    },
    { 
      id: 5, 
      name: "TIBS Technologies", 
      logo: "tibs.png", 
      type: "client",
      size: 130,
      color: "rgba(231, 76, 60, 0.2)"
    },
    { 
      id: 6, 
      name: "Digital Solutions", 
      logo: "digital.jpg", 
      type: "client",
      size: 95,
      color: "rgba(52, 73, 94, 0.2)"
    },
    { 
      id: 7, 
      name: "Brought Digital", 
      logo: "brought.png", 
      type: "partner",
      size: 150,
      color: "rgba(26, 188, 156, 0.2)"
    },
    { 
      id: 8, 
      name: "Flensi Enterprises", 
      logo: "flensi.jpeg", 
      type: "client",
      size: 95,
      color: "rgba(230, 126, 34, 0.2)"
    },
  ];

  const featuredPartner = {
    id: 9,
    name: "DeQuest Technologies",
    logo: "dq.jpeg",
    type: "strategic partner",
    description: "Strategic Technology Partner",
    size: 160,
    color: "rgba(105, 54, 244, 0.2)"
  };

  // Check for mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Desktop physics animation
  useEffect(() => {
    if (isMobile || !containerRef.current) return;

    // Card entrance animation
    if (cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { y: 100, opacity: 0, scale: 0.9 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }

    // Initialize physics
    setTimeout(() => {
      initializePhysics();
    }, 500);

    return () => {
      velocitiesRef.current = [];
    };
  }, [isMobile]);

  const initializePhysics = () => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;
    const allLogos = [...logos, featuredPartner];

    allLogos.forEach((logo, index) => {
      const logoElement = logosRef.current[index];
      if (!logoElement) return;

      const radius = logo.size / 2;
      const padding = radius + 20;

      // Random starting position
      const randomX = padding + Math.random() * (containerWidth - padding * 2);
      const randomY = padding + Math.random() * (containerHeight - padding * 2);

      gsap.set(logoElement, {
        x: randomX,
        y: randomY,
        opacity: 0,
      });

      // Fade in with stagger
      gsap.to(logoElement, {
        opacity: 1,
        duration: 0.6,
        delay: index * 0.1,
      });

      // Initialize velocity (faster, livelier)
      const angle = Math.random() * Math.PI * 2;
      const speed = 3.5 + Math.random() * 3;
      velocitiesRef.current[index] = {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      };
    });

    // Start physics loop
    updatePhysics();
  };

  const updatePhysics = () => {
    if (!containerRef.current || isMobile) return;

    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;
    const allLogos = [...logos, featuredPartner];

    allLogos.forEach((logo, index) => {
      const logoElement = logosRef.current[index];
      if (!logoElement || !velocitiesRef.current[index]) return;

      const radius = logo.size / 2;
      let { vx, vy } = velocitiesRef.current[index];

      const currentX = parseFloat(gsap.getProperty(logoElement, "x") || 0);
      const currentY = parseFloat(gsap.getProperty(logoElement, "y") || 0);

      let newX = currentX + vx;
      let newY = currentY + vy;

      // Wall collision
      if (newX - radius <= 0 || newX + radius >= containerWidth) {
        vx *= -0.9;
        newX = Math.max(radius, Math.min(containerWidth - radius, newX));
        // Add bounce effect
        gsap.to(logoElement, { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 });
      }

      if (newY - radius <= 0 || newY + radius >= containerHeight) {
        vy *= -0.9;
        newY = Math.max(radius, Math.min(containerHeight - radius, newY));
        // Add bounce effect
        gsap.to(logoElement, { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 });
      }

      // Check collision with other logos
      allLogos.forEach((otherLogo, otherIndex) => {
        if (otherIndex === index) return;

        const otherElement = logosRef.current[otherIndex];
        if (!otherElement || !velocitiesRef.current[otherIndex]) return;

        const otherRadius = otherLogo.size / 2;
        const otherX = parseFloat(gsap.getProperty(otherElement, "x") || 0);
        const otherY = parseFloat(gsap.getProperty(otherElement, "y") || 0);

        const dx = newX - otherX;
        const dy = newY - otherY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const minDistance = radius + otherRadius;

        if (distance < minDistance) {
          // Collision detected - elastic bounce
          const angle = Math.atan2(dy, dx);
          const targetX = otherX + Math.cos(angle) * minDistance;
          const targetY = otherY + Math.sin(angle) * minDistance;

          // Separate the circles
          newX = targetX;
          newY = targetY;

          // Exchange velocities (simplified elastic collision)
          const tempVx = vx;
          const tempVy = vy;
          vx = velocitiesRef.current[otherIndex].vx * 0.9;
          vy = velocitiesRef.current[otherIndex].vy * 0.9;
          velocitiesRef.current[otherIndex].vx = tempVx * 0.9;
          velocitiesRef.current[otherIndex].vy = tempVy * 0.9;

          // Bounce effect
          gsap.to([logoElement, otherElement], {
            scale: 0.95,
            duration: 0.1,
            yoyo: true,
            repeat: 1,
          });
        }
      });

      // Update velocity
      velocitiesRef.current[index] = { vx, vy };

      // Apply movement
      gsap.to(logoElement, {
        x: newX,
        y: newY,
        duration: 0.016,
        ease: "none",
      });
    });

    requestAnimationFrame(updatePhysics);
  };

  const handleLogoClick = (logo) => {
    setSelectedLogo(logo);
  };

  // Mobile view - don't render physics, just grid
  if (isMobile) {
    return (
      <section className="relative py-0 pb-16 bg-[#0F0C1F]" style={{ marginTop: '-150px', paddingTop: '200px' }} id="clients-partners">
        <style>{`
          @media (max-width: 768px) {
            #clients-partners {
              margin-top: 0 !important;
              padding-top: 3rem !important;
            }
          }
        `}</style>
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            {/* <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(105, 54, 244, 0.1), rgba(139, 92, 246, 0.1))",
                border: "1px solid rgba(105, 54, 244, 0.2)",
              }}
            >
              <i className="fa-solid fa-handshake" style={{ color: "#6936F4" }}></i>
              <span className="text-sm font-semibold" style={{ color: "#6936F4" }}>
                Trusted By
              </span>
            </div> */}
            <h2 className="text-3xl font-bold mb-3" style={{ color: "#ffffff" }}>
              Clients and Partners
            </h2>
          </div>

          {/* Mobile: Horizontal scrolling logos */}
          <div className="overflow-hidden">
            <div className="mobile-scroll-container">
              <div className="mobile-scroll-track">
                {[...logos, featuredPartner, ...logos].map((logo, index) => (
                  <div
                    key={`mobile-${index}`}
                    className="mobile-logo-item"
                    onClick={() => handleLogoClick(logo)}
                  >
                    <div className="mobile-logo-bubble">
                      <img
                        src={process.env.PUBLIC_URL + `/images/clients/${logo.logo}`}
                        alt={logo.name}
                        className="mobile-logo-image"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile info text */}
          {/* spacer */}
        </div>

        {/* Modal */}
        {selectedLogo && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setSelectedLogo(null)}
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-sm mx-4 shadow-2xl animate-scaleIn"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-gray-900">{selectedLogo.name}</h3>
                <button onClick={() => setSelectedLogo(null)} className="text-gray-400">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-4 flex items-center justify-center">
                <img
                  src={process.env.PUBLIC_URL + `/images/clients/${selectedLogo.logo}`}
                  alt={selectedLogo.name}
                  className="max-h-24 object-contain"
                />
              </div>
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                  selectedLogo.type === 'partner' || selectedLogo.type === 'strategic partner'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {selectedLogo.type === 'strategic partner' ? 'Strategic Partner' : 
                 selectedLogo.type === 'partner' ? 'Partner' : 'Client'}
              </span>
            </div>
          </div>
        )}
      </section>
    );
  }

  // Desktop view with physics
  return (
    <section className="py-16 bg-white" id="clients-partners">
      <div className="container mx-auto px-4">
        {/* Main Card Container - Floating FIRST */}
        <div className="relative mx-auto" style={{ maxWidth: '640px', marginTop: '-340px', zIndex: 10 }}>
          {/* Header INSIDE the floating container */}
          <div className="text-center mb-6">
            {/* <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(105, 54, 244, 0.1), rgba(139, 92, 246, 0.1))",
                border: "1px solid rgba(105, 54, 244, 0.2)",
              }}
            >
              <i className="fa-solid fa-handshake" style={{ color: "#6936F4" }}></i>
              <span className="text-sm font-semibold" style={{ color: "#6936F4" }}>
                Trusted By
              </span>
            </div> */}
            <h2 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: "#ffffff" }}>
              Clients and Partners
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Collaborating with industry leaders to deliver exceptional ICT solutions
            </p>
          </div>
          <div
            ref={cardRef}
            className="clients-card"
          >
            {/* Physics Container */}
            <div
              ref={containerRef}
              className="clients-physics-container"
            >
              {/* All Logos as Bubbles */}
              {[...logos, featuredPartner].map((logo, index) => (
                <div
                  key={logo.id}
                  ref={(el) => (logosRef.current[index] = el)}
                  className="logo-bubble"
                  style={{
                    width: `${logo.size}px`,
                    height: `${logo.size}px`,
                  }}
                  onClick={() => handleLogoClick(logo)}
                >
                  <div
                    className="logo-bubble-inner"
                    style={{
                      background: logo.type === 'partner' || logo.type === 'strategic partner'
                        ? `linear-gradient(135deg, ${logo.color}, rgba(139, 92, 246, 0.3))`
                        : `linear-gradient(135deg, ${logo.color}, rgba(52, 152, 219, 0.3))`,
                    }}
                  >
                    <img
                      src={process.env.PUBLIC_URL + `/images/clients/${logo.logo}`}
                      alt={logo.name}
                      className="logo-image"
                    />
                    {/* Partner badge */}
                    {(logo.type === 'partner' || logo.type === 'strategic partner') && (
                      <div className="partner-badge">
                        <i className="fas fa-star"></i>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Helper text */}
          {/* <p className="text-center text-gray-600 mt-6 text-sm">
            Click any logo to learn more • Logos bounce and interact with each other
          </p> */}
        </div>
        {/* End of floating container */}
      </div>
      {/* End of main container */}

      {/* Modal */}
      {selectedLogo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setSelectedLogo(null)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl animate-scaleIn border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedLogo.name}</h3>
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold mt-2 ${
                    selectedLogo.type === 'partner' || selectedLogo.type === 'strategic partner'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  <i
                    className={`fas ${
                      selectedLogo.type === 'strategic partner'
                        ? 'fa-star'
                        : selectedLogo.type === 'partner'
                        ? 'fa-handshake'
                        : 'fa-building'
                    }`}
                  ></i>
                  {selectedLogo.type === 'strategic partner'
                    ? 'Strategic Partner'
                    : selectedLogo.type === 'partner'
                    ? 'Partner'
                    : 'Client'}
                </span>
              </div>
              <button onClick={() => setSelectedLogo(null)} className="text-gray-400 hover:text-gray-600 text-xl">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 mb-4 flex items-center justify-center">
              <img
                src={process.env.PUBLIC_URL + `/images/clients/${selectedLogo.logo}`}
                alt={selectedLogo.name}
                className="max-w-full max-h-32 object-contain"
              />
            </div>
            {selectedLogo.description && (
              <p className="text-gray-600 text-center">{selectedLogo.description}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default ClientsPartners;